/**
 * TrendChart.tsx — multi-series line chart for trend cards.
 *
 * Render-guard: when there are < 2 points, shows an `<EmptyTrendNotice/>`.
 * The DB currently has only one snapshot — see PHASE2_SPEC §1.
 */

import React from 'react';
import {
	CartesianGrid,
	Line,
	LineChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from 'recharts';

import EmptyTrendNotice from './EmptyTrendNotice';
import { shortDay } from '../helpers/dateFormat';

export interface TrendSeries {
	dataKey: string;
	name: string;
	color: string;
}

interface Props {
	data: Record<string, number | string>[];
	xKey: string;
	series: TrendSeries[];
	height?: number;
	asOf?: string | null;
	valueFormatter?: (v: number) => string;
}

export default function TrendChart({
	data,
	xKey,
	series,
	height = 280,
	asOf,
	valueFormatter,
}: Props) {
	const safe = Array.isArray(data) ? data : [];
	if (safe.length < 2) {
		return <EmptyTrendNotice asOf={asOf} />;
	}

	return (
		<div style={{ width: '100%', height }}>
			<ResponsiveContainer width="100%" height="100%">
				<LineChart data={safe} margin={{ top: 8, right: 12, bottom: 8, left: 0 }}>
					<CartesianGrid strokeDasharray="3 3" stroke="var(--ink-100)" />
					<XAxis
						dataKey={xKey}
						tick={{ fontSize: 11, fill: 'var(--text-dim)' }}
						tickFormatter={(v: string) => shortDay(v)}
					/>
					<YAxis
						tick={{ fontSize: 11, fill: 'var(--text-dim)' }}
						tickFormatter={(v: number) =>
							valueFormatter ? valueFormatter(v).replace('₹ ', '').replace(' cr', '') : String(v)
						}
					/>
					<Tooltip
						labelFormatter={(label: string) => shortDay(label)}
						formatter={(v: number | string) =>
							typeof v === 'number' && valueFormatter ? valueFormatter(v) : String(v)
						}
					/>
					{series.map((s) => (
						<Line
							key={s.dataKey}
							type="monotone"
							dataKey={s.dataKey}
							name={s.name}
							stroke={s.color}
							strokeWidth={2}
							dot={false}
						/>
					))}
				</LineChart>
			</ResponsiveContainer>
		</div>
	);
}
